import {
  Card,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  Box,
  Divider,
  IconButton,
  Icon,
  Tooltip as MuiTooltip,
} from "@mui/material";
// Chart.js related imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
// import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
// Material Dashboard 2 React example components
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import MDTypography from "components/MDTypography";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useEffect, useState } from "react";
import {
  getTopDashboardUser,
  getUserDashboardPageData,
  getUserDashboardPageOtherData,
} from "services/api";
import { useAuth } from "context/authContext";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import VerticalBarChart2 from "examples/Charts/BarCharts/VerticalBarChart/index2";
import LoadingScreen from "components/Loading";
import GaugeChart from "react-gauge-chart"; // Make sure to import GaugeChart
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import PerformanceGaugeCard from "../PerformanceGauge";
import StepRoadmap from "components/SuccessionPlan";
import SuccessionStepper from "layouts/admin/SuccessionPlanSteps/components/Successors/Steps";
import SuccessionPlanTrainingUserDialog from "components/SuccessorTrainingUserDialog";

function UserDashboard() {
  const { t, i18n } = useTranslation();

  const [topCardsData, setTopCardsData] = useState([]);

  const [databyEmp, setDataByEmp] = useState([]);
  const [jobLoading, setJobLoading] = useState(false);
  //Top Chart
  const [performanceData, setPerformanceData] = useState([]);
  const [showDepartmentFilter, setShowDepartmentFilter] = useState(true);
  const [performanceDataJob, setPerformanceDataJob] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [departments, setDepartments] = useState([]);
  const [successionPlan, setSuccessionPlan] = useState([]);

  const { employee, selectedMonth, selectedYear, quarter, isQuarterly } = useAuth();

  const [jobFilter, setJobFilter] = useState("");
  const [gaugeValue, setGaugeValue] = useState(0);
  const [jobs, setJobs] = useState([]);

  const [distribution, setDistribution] = useState();
  const [loading, setLoading] = useState(true);
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [selectedPlanForDialog, setSelectedPlanForDialog] = useState(null);

  const transformDataForChart = (departmentResults) => {
    const labels = departmentResults.map((item) => item.departmentName);
    const data = departmentResults.map((item) => item.averageResult);

    return {
      labels, // Department names
      datasets: [
        {
          label: t("user.dashboards.averagePerformanceByJob"),
          data, // Average results
          backgroundColor: data.map((value) => {
            if (value > 105) return "blue";
            if (value > 90) return "green";
            if (value > 80) return "yellow";
            if (value > 70) return "orange";
            return "red";
          }),
          borderWidth: 1,
        },
      ],
    };
  };

  const handleOpenTrainings = (plan) => {
    setSelectedPlanForDialog(plan); // Store the clicked plan data
    setTrainingDialogOpen(true); // Open the dialog
  };

  useEffect(() => {
    if (!employee) return; // exit early if employee not loaded yet

    const fetchData = async () => {
      const data = await getUserDashboardPageData(employee.departmentId, employee.id);
      // Show department filter only if role is 1
      setShowDepartmentFilter(employee.role === 1);

      if (employee.role !== 1) {
        if (data.departmentChosen && data.departmentChosen.length > 0) {
          setDepartmentFilter(data.departmentChosen[0].departmentId);
        } else {
          setDepartmentFilter(null);
        }

        if (data.successionPlans && Object.keys(data.successionPlans).length > 0) {
          const mappedPlans = Object.entries(data.successionPlans).map(([key, value]) => ({
            successorId: key,
            targetJobId: value.targetJobId,
            targetJobName: value.targetJobName,
            steps: value.steps || [],
          }));

          setSuccessionPlan(mappedPlans);
          // const mappedPlans = data.successionPlans.map((plan) => ({
          //   succPlanId: plan[0],
          //   trainingId: plan[1],
          //   date: plan[2],
          //   employeeName: plan[3],
          //   trainingName: plan[4],
          //   planJobId: plan[5],
          //   planJobName: plan[6],
          //   currentJobName: plan[7],
          //   employeeUuid: plan[8],
          // }));
          // setSuccessionPlan(mappedPlans);
        } else {
          setSuccessionPlan([]);
        }
      } else {
        if (data.departments && data.departments.length > 0) {
          setDepartments(
            data.departments.map((dep) => ({
              id: dep.departmentId,
              name: dep.departmentName,
            }))
          );
        }
      }
    };
    fetchData();
  }, [employee]); // add employee as dependency

  useEffect(() => {
    if (!databyEmp) return;

    let filtered = databyEmp;

    // ✅ Filter by department first
    if (departmentFilter && departmentFilter !== "All") {
      filtered = filtered.filter((e) => e.newDepartmentId === departmentFilter);
    }

    // ✅ Then filter by job if selected
    if (jobFilter) {
      filtered = filtered.filter((e) => e.jobName === jobFilter);
    }

    setPerformanceDataJob(transformDataForChart(filtered));
  }, [jobFilter, databyEmp, departmentFilter]); // ✅ Added departmentFilter to dependencies

  //Use Effect
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setJobFilter("");
      setGaugeValue(0);

      try {
        const isCEO = employee?.role === 1; //CEO
        const employeeId = employee?.id;
        const month = isQuarterly
          ? quarter === 1
            ? 3
            : quarter === 2
              ? 6
              : quarter === 3
                ? 9
                : 12
          : selectedMonth;
        const year = selectedYear;

        let currentDept;
        if (departmentFilter === "All") {
          currentDept = null; // treat "All Departments" as no specific filter
        } else {
          currentDept = Number(departmentFilter);
        }
        const departmentId = isCEO
          ? currentDept // null = ALL departments
          : employee?.departmentId;

        // Set up the dynamic top dashboard call
        const data = await getUserDashboardPageOtherData(
          year,
          month,
          departmentId,
          employeeId,
          isCEO
        );

        let topCardsData = [];

        if (isCEO) {
          if (currentDept) {
            // CEO selected a specific department
            const deptExists = departments.some((dep) => Number(dep.id) === Number(currentDept));
            topCardsData = deptExists
              ? await getTopDashboardUser(month, year, currentDept, employeeId)
              : [];
          } else {
            // CEO selected "All Departments"
            topCardsData = await getTopDashboardUser(month, year); // all top cards
          }
        } else {
          // Normal user → always their department
          topCardsData = await getTopDashboardUser(month, year, employee?.departmentId, employeeId);
        }

        setTopCardsData(topCardsData);

        if (data?.byEmployee) {
          let filteredEmpData = data.byEmployee;

          if (currentDept) {
            filteredEmpData = filteredEmpData.filter((e) => e.newDepartmentId === currentDept);
          }

          setDataByEmp(filteredEmpData);

          const scores = filteredEmpData
            .map((e) => Math.round(e.averageResult))
            .filter((s) => s !== 0);

          const count = {};
          scores.forEach((s) => (count[s] = (count[s] || 0) + 1));

          const uniqueJobs = [...new Set(filteredEmpData.map((e) => e.jobName))];
          setJobs(uniqueJobs);
        }

        if (data?.byJob) {
          let filteredJobData = data.byJob;

          if (currentDept) {
            const deptObj = departments.find((d) => d.id === currentDept);
            filteredJobData = filteredJobData.filter((e) => e.depName === deptObj?.name);
          }

          setPerformanceData(transformDataForChart(filteredJobData));
        }

        if (data?.byDepartment) {
          const matchedDepartment = currentDept
            ? data.byDepartment.find((dept) => String(dept.departmentId) === String(currentDept))
            : data.byDepartment.find(
                (dept) => String(dept.departmentId) === String(employee?.departmentId)
              );

          setGaugeValue(matchedDepartment?.averageResult || 0);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMonth, selectedYear, departmentFilter, isQuarterly, quarter]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <MDBox pt={3}>
      <Grid container spacing={2} mb={3} alignItems="center" justifyContent="center">
        {/* Department Selector (Conditionally Rendered) */}
        {showDepartmentFilter && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("user.department")}
              value={departmentFilter}
              // onChange={(e) => handleDepartmentChange(e.target.value)}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              sx={fieldStyles} // Apply consistent styling
            >
              <MenuItem value="All">{t("user.dashboards.allDeps")}</MenuItem>
              {departments.map((dep) => (
                <MenuItem key={dep.id} value={dep.id}>
                  {dep.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>

      <>
        {/* Top summary cards */}
        <Grid container spacing={2}>
          {[
            {
              color: "info",
              icon: <ArrowUpwardIcon />,
              title: t("user.dashboards.overperforming"),
              count: topCardsData?.[0] ?? 0,
            },
            {
              color: "error",
              icon: <ArrowDownwardIcon />,
              title: t("user.dashboards.underperforming"),
              count: topCardsData?.[1] ?? 0,
              sx: { backgroundColor: "#FF0000", color: "#FFFFFF" },
            },
            {
              color: "success",
              icon: "speed",
              title: t("user.dashboards.jobsHavingScorecard"),
              count: topCardsData?.[2] ?? 0,
            },
            {
              color: "secondary",
              icon: "work",
              title: t("user.dashboards.totalJobs"),
              count: topCardsData?.[3] ?? 0,
            },
          ].map((card, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard {...card} />
              </MDBox>
            </Grid>
          ))}
        </Grid>

        {/* <Box mt={2}>
          <StepRoadmap trainings={successionPlan} />
        </Box> */}
        <MDBox mt={2}>
          {successionPlan.length > 0
            ? successionPlan.map((plan) => (
                <Card
                  key={plan.successorId} // Use successorId as the unique key
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "none",
                  }}
                >
                  <MDBox mb={1} display="flex" flexDirection="column">
                    <MDTypography variant="h6" fontWeight="bold" textAlign="center">
                      {t("admin.successionPlanSteps.successionPlan")}
                    </MDTypography>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                      <MDTypography variant="body2" fontWeight="bold">
                        {plan.targetJobName}
                      </MDTypography>
                      <MuiTooltip title={t("admin.successionPlanSteps.viewTrainings")} arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenTrainings(plan)}
                          sx={{
                            ml: 1.5,
                            bgcolor: "#e3f2fd",
                            color: "#1976d2",
                            "&:hover": { bgcolor: "#bbdefb" },
                            transition: "all 0.2s",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <Icon sx={{ fontSize: "1.2rem !important" }}>school</Icon>
                        </IconButton>
                      </MuiTooltip>
                    </Box>
                  </MDBox>

                  {/* Pass the steps array to the Stepper component */}
                  <SuccessionStepper steps={plan.steps} />
                </Card>
              ))
            : /* If no plans, you can show a message or nothing */
              null}
        </MDBox>

        {/* Main chart layout */}
        <Grid container spacing={2} alignItems="stretch">
          {/* TOP ROW */}
          <Grid item xs={12} container spacing={2} alignItems="stretch">
            {/* Left - Average Performance */}
            <Grid item xs={12} md={8} sx={{ display: "flex" }}>
              <Card
                sx={{
                  height: 200,
                  width: "100%",
                  borderRadius: "4px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                }}
              >
                <MDBox p={2}>
                  <MDTypography variant="h6" textAlign="center">
                    {t("user.dashboards.averagePerformanceByJob")}
                  </MDTypography>

                  <MDBox>
                    {performanceData ? (
                      <VerticalBarChart2 height={160} chart={performanceData} />
                    ) : (
                      <Box display="flex" justifyContent="center" p={1}>
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            {/* Right - Gauge (MATCHES HEIGHT NOW) */}
            <Grid item xs={12} md={4} sx={{ display: "flex" }}>
              <Card
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column", // 🔥 KEY FIX
                  justifyContent: "center",
                  borderRadius: "4px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <MDTypography
                    variant="h6"
                    gutterBottom
                    textAlign="center"
                    sx={{
                      pt: 2, // 🔥 space from top
                    }}
                  >
                    {t("user.dashboards.depPer")}
                  </MDTypography>

                  <PerformanceGaugeCard
                    percent={gaugeValue / 100}
                    arcWidth={0.2}
                    arcPadding={0.02}
                    needleColor="#000"
                    needleBaseColor="#000"
                    textColor="#000"
                    // gaugeStyle={{ width: "100%", height: "100%" }}
                    gaugeStyle={{
                      width: "100%",
                      maxWidth: 220, // 🔥 controls width
                      maxHeight: 220, // 🔥 controls height
                      margin: "0 auto", // 🔥 keeps it centered
                    }}
                  />
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* SECOND ROW (ONLY LEFT SIDE) */}
          <Grid item xs={12} md={8}>
            {/* Employee Performance Chart by Job */}
            <Card
              sx={{
                height: 250,
                mt: 0,
                overflow: "hidden",
                borderRadius: "4px",
                border: "1px solid #e5e7eb",
                boxShadow: "none",
              }}
            >
              <MDBox p={2}>
                <MDTypography variant="h6" sx={{ textAlign: "center", width: "100%" }}>
                  {t("user.dashboards.employeePerformanceChart")}
                </MDTypography>
                <FormControl size="small" variant="outlined" sx={{ mt: 1, mb: 1, minWidth: 150 }}>
                  <InputLabel id="job-select-label">{t("user.dashboards.job")}</InputLabel>
                  <Select
                    labelId="job-select-label"
                    value={jobFilter}
                    // onChange={(e) => handleJobChange(e.target.value)}
                    onChange={(e) => setJobFilter(e.target.value)}
                    label="Job"
                    sx={fieldStyles}
                  >
                    <MenuItem value="">{t("user.dashboards.allJobs")}</MenuItem>
                    {jobs?.map((job) => (
                      <MenuItem key={job} value={job}>
                        {job}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {performanceDataJob && (
                  <VerticalBarChart2 height={160} chart={performanceDataJob} />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        {selectedPlanForDialog && (
          <SuccessionPlanTrainingUserDialog
            open={trainingDialogOpen}
            onClose={() => setTrainingDialogOpen(false)}
            plan={selectedPlanForDialog}
          />
        )}
      </>
    </MDBox>
  );
}
export default UserDashboard;
